/** The person's own bookings, grouped by what they need to do about them. */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { Icon } from "@/components/Icon";
import { EmptyState, ErrorState, SkeletonRows } from "@/components/States";
import { StatusPill } from "@/components/StatusPill";
import { api } from "@/lib/api";
import {
  durationBetween,
  formatDate,
  formatDuration,
  formatTime,
  sourceLabel,
  statusLabel,
  statusTone,
} from "@/lib/format";
import type { Booking } from "@/lib/types";

type Scope = "upcoming" | "pending" | "past" | "cancelled";

const TABS: { key: Scope; label: string }[] = [
  { key: "upcoming", label: "Upcoming" },
  { key: "pending", label: "Waiting for approval" },
  { key: "past", label: "Past" },
  { key: "cancelled", label: "Cancelled" },
];

const EMPTY: Record<Scope, { title: string; message: string }> = {
  upcoming: {
    title: "No upcoming meetings",
    message: "When you book a room it will appear here, with check-in and extension.",
  },
  pending: {
    title: "Nothing waiting for approval",
    message: "Requests for rooms that need sign-off appear here until they are decided.",
  },
  past: {
    title: "No past meetings yet",
    message: "Meetings you have held will be listed here.",
  },
  cancelled: {
    title: "Nothing cancelled",
    message: "Cancelled and declined bookings are kept here for reference.",
  },
};

export function MyBookings() {
  const [scope, setScope] = useState<Scope>("upcoming");

  const query = useQuery({
    queryKey: ["portal-bookings", scope],
    queryFn: () =>
      api.get<{ bookings: Booking[]; counts: { upcoming: number; pending: number } }>(
        "/portal/bookings",
        { scope },
      ),
  });

  const bookings = query.data?.bookings ?? [];

  return (
    <div className="page page--narrow">
      <div className="page__head">
        <div className="page__title">
          <h1 className="t-h1">My bookings</h1>
          <p className="t-body ink-secondary">
            Check in, extend, or hand a room back when you finish early.
          </p>
        </div>
        <Link className="btn btn--primary" to="/">
          <Icon name="plus" size={16} />
          Book a room
        </Link>
      </div>

      <div className="tabs" role="tablist" aria-label="Booking status">
        {TABS.map((tab) => (
          <button key={tab.key} type="button" role="tab" className="tab"
            aria-selected={scope === tab.key} onClick={() => setScope(tab.key)}>
            {tab.label}
            {tab.key === "pending" && (query.data?.counts.pending ?? 0) > 0 && (
              <span className="pill pill--soon" style={{ marginLeft: 8 }}>
                <span className="pill__dot" aria-hidden="true" />
                {query.data!.counts.pending}
              </span>
            )}
          </button>
        ))}
      </div>

      <div style={{ marginTop: "var(--space-5)" }}>
        {query.isPending ? (
          <SkeletonRows rows={4} height={92} />
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => query.refetch()} />
        ) : bookings.length === 0 ? (
          <EmptyState
            icon="calendar"
            title={EMPTY[scope].title}
            message={EMPTY[scope].message}
            action={
              scope === "upcoming" ? (
                <Link className="btn btn--primary" to="/">Find a room</Link>
              ) : undefined
            }
          />
        ) : (
          <ul className="stack" style={{ gap: "var(--space-3)" }}>
            {bookings.map((booking) => (
              <li key={booking.id}>
                <Link to={`/bookings/${booking.id}`} className="card" style={{
                  display: "flex", gap: "var(--space-4)", padding: "var(--space-5)",
                  alignItems: "flex-start", textDecoration: "none", color: "inherit",
                }}>
                  <div className="stack" style={{
                    gap: 2, minWidth: 72, alignItems: "flex-start",
                  }}>
                    <span className="t-label ink-tertiary">
                      {formatDate(booking.start_at)}
                    </span>
                    <span className="t-h3" style={{ fontFamily: "var(--font-mono)" }}>
                      {formatTime(booking.start_at)}
                    </span>
                  </div>
                  <div className="stack grow" style={{ gap: 4, minWidth: 0 }}>
                    <span className="t-h3 truncate">{booking.subject}</span>
                    <span className="t-body ink-secondary truncate">
                      {booking.room_name}
                      {booking.location_name ? ` · ${booking.location_name}` : ""}
                      {" · "}
                      {formatDuration(durationBetween(booking.start_at, booking.end_at))}
                    </span>
                    <span className="t-caption ink-tertiary">
                      {sourceLabel(booking.source)}
                      {booking.booked_for && booking.booked_for.id !== booking.organizer?.id
                        ? ` · for ${booking.booked_for.display_name}`
                        : ""}
                      {booking.series_id ? " · part of a series" : ""}
                    </span>
                  </div>
                  <div className="stack" style={{ gap: "var(--space-2)", alignItems: "flex-end" }}>
                    <StatusPill tone={statusTone(booking.status)}>
                      {statusLabel(booking.status)}
                    </StatusPill>
                    {booking.sync.status === "failed" && (
                      <span className="t-caption" style={{ color: "var(--status-soon-soft-ink)" }}>
                        Calendar not updated
                      </span>
                    )}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
